<script setup>
import WorkflowsDataTableList from "@/components/workflows/WorkflowsDataTableList.vue";
import { onMounted, ref, computed, watch } from "vue";
import axios from "@/utils/axios";
import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";
import { useLoading } from "vue-loading-overlay";
import loaderData from "@/utils/loader-data";
import { useRouter } from "vue-router";
import { buildCsv, downloadCsv, buildExportFilename } from "@/utils/csvExport";

const router = useRouter();
const $loading = useLoading(loaderData);
const errors = ref([]);

const isAdminOrSuperAdmin = ref(false);
const hasViewWorkflowsPermission = ref(false);
const currentTab = ref("my");
const workflowCategories = ref([]);
const isAvailableWorkflowModalVisible = ref(false);
const selectedWorkflowCategory = ref(null);
const availableWorkflows = ref({ data: [], total: 0 });
const availableWorkflowFilterInfo = ref({ page: 1, perPage: 10 });
const isLicenseErrorModalVisible = ref(false);
const licenseErrorMessage = ref('');

onMounted(() => {
  const userData = JSON.parse(localStorage.getItem('userData'));
  isAdminOrSuperAdmin.value = ['super_admin', 'admin'].includes(userData.role);
  
  // Check for workflows permission
  const userPermissionsByRoles = userData.permissions_by_roles;
  const viewWorkflowsPermission = Object.values(
    userPermissionsByRoles
  ).some((permissions) => permissions.includes("View Workflows"));
  
  if (viewWorkflowsPermission) {
    hasViewWorkflowsPermission.value = true;
  }
});

const baseHeaders = [
  {
    title: "Title",
    key: "title",
  },
  {
    title: "Description",
    key: "description",
    sortable: false,
  },
  {
    title: "Type",
    key: "type",
    sortable: false,
  },
  {
    title: "Status",
    key: "status",
    sortable: false,
  },
  {
    title: "Signature Status",
    key: "signature_status",
    sortable: false,
  },
];

const headers = computed(() => {
  let dynamicHeaders = [...baseHeaders];
  
  // Remove status column for available workflows tab
  if (currentTab.value === 'available') {
    dynamicHeaders = dynamicHeaders.filter(header => header.key !== 'status');
  }

  return [
    ...dynamicHeaders,
    // Only show user column for admin/super_admin or assignment-related tabs, but not for available workflows
    ...(((isAdminOrSuperAdmin.value || ['approvals', 'assigned', 'completed'].includes(currentTab.value)) && currentTab.value !== 'available') ? [{
      title: "User",
      key: "user",
      sortable: false,
    }] : []),
    {
      title: "Actions",
      key: "actions",
      sortable: false,
    },
  ];
});

const workflows = ref([]);
const filterInfo = ref({});

const availableWorkflowModalTitle = computed(() => {
  if (!selectedWorkflowCategory.value) {
    return "Available Workflows";
  }

  return `${selectedWorkflowCategory.value.name} Workflows`;
});

const normalizeListData = (responseData) => {
  if (Array.isArray(responseData)) {
    return responseData;
  }

  if (Array.isArray(responseData?.data)) {
    return responseData.data;
  }

  return [];
};

const startWorkflow = (item) => {
  // If this is from the available workflows tab, start the workflow for the user
  if (currentTab.value === 'available') {
    startAvailableWorkflow(item);
  } else {
    // Otherwise navigate to process/view the workflow
    router.push({ name: 'workflows-process-id', params: { id: item.id } });
  }
};

const viewWorkflow = (item) => {
  router.push({ name: 'workflows-view-id', params: { id: item.id } });
};

const goToApprovalStep = ({ workflow, step }) => {
  if (!workflow?.id || !step?.id) {
    return;
  }

  const targetRoute = {
    name: 'workflows-process-id',
    params: { id: workflow?.id },
    query: { step_id: step?.id },
  };

  // Use hard navigation to avoid router/component update race in approval table context.
  const resolvedRoute = router.resolve(targetRoute);
  window.location.assign(resolvedRoute.href);
};

const startAvailableWorkflow = async (item) => {
  let loader = $loading.show();
  try {
    const response = await axios.post(`/workflows/${item.id}/start-for-self`);
    if (response.status === "success") {
      closeAvailableWorkflowModal();
      toast.success('Workflow started successfully! You can now access it in "My Workflows" tab.', {
        autoClose: 3000,
      });
      // Switch to "My Workflows" tab and refresh
      currentTab.value = 'my';
    } else {
      // Show error popup for license-related errors
      if (response.message && response.message.toLowerCase().includes('license')) {
        licenseErrorMessage.value = response.message;
        isLicenseErrorModalVisible.value = true;
      } else {
        toast.error(response.message, {
          autoClose: 2000,
        });
      }
    }
  } catch (error) {
    console.error('Error starting workflow:', error);
    const errorMessage = error.response?.data?.message || 'Failed to start workflow.';
    // Show error popup for license-related errors
    if (errorMessage.toLowerCase().includes('license')) {
      licenseErrorMessage.value = errorMessage;
      isLicenseErrorModalVisible.value = true;
    } else {
      toast.error(errorMessage, {
        autoClose: 2000,
      });
    }
  } finally {
    loader.hide();
  }
};

const getApiEndpoint = () => {
  switch (currentTab.value) {
    case 'all':
      return '/all_workflows';
    case 'completed':
      return '/completed_workflows';
    case 'my':
      return '/user_workflows';
    case 'approvals':
      return '/assigned_workflows';
    case 'assigned':
      return '/task_assigned_workflows';
    case 'available':
      return '/available_workflows';
    default:
      return '/user_workflows';
  }
};

// Columns included in the CSV export
const exportColumns = [
  { key: 'id', title: 'ID' },
  { key: 'title', title: 'Title' },
  { key: 'description', title: 'Description' },
  { key: 'type', title: 'Type' },
  { key: 'status', title: 'Status' },
  { key: 'workflow_category', title: 'Category' },
  { key: 'user', title: 'Owner' },
  { key: 'created_at', title: 'Created At' },
  { key: 'updated_at', title: 'Updated At' },
];

// Normalize a workflow row into flat values for the CSV columns
const normalizeExportRow = (workflow) => {
  const category = workflow?.workflow_category;
  const owner = workflow?.user;

  return {
    id: workflow?.id,
    title: workflow?.title,
    description: workflow?.description,
    type: workflow?.type,
    status: workflow?.status,
    workflow_category: category?.name,
    user: owner?.name,
    created_at: workflow?.created_at,
    updated_at: workflow?.updated_at,
  };
};

// Fetch all workflows matching the active tab/filter/search (no pagination)
const fetchAllWorkflowsForExport = async () => {
  const endpoint = getApiEndpoint();
  const search = filterInfo.value?.search?.trim();

  let url = `${endpoint}?page=1&per_page=100000`;
  if (search) {
    url += `&search=${encodeURIComponent(search)}`;
  }

  const response = await axios.get(url);
  if (response.status !== "success") {
    throw new Error(response.message || 'Failed to fetch workflows for export.');
  }

  return normalizeListData(response?.data?.workflows);
};

// Export the currently filtered workflows to a CSV file
const exportWorkflows = async () => {
  let loader = $loading.show();
  try {
    const rows = await fetchAllWorkflowsForExport();

    if (rows.length === 0) {
      toast.info('No workflows found to export.', {
        autoClose: 2000,
      });
      return;
    }

    const normalizedRows = rows.map(normalizeExportRow);
    const csvContent = buildCsv(normalizedRows, exportColumns);
    const filename = buildExportFilename('workflows-export');

    downloadCsv(csvContent, filename);

    toast.success(`Exported ${rows.length} workflow(s) to ${filename}.`, {
      autoClose: 3000,
    });
  } catch (error) {
    console.error('Error exporting workflows:', error);
    const errorMessage = error?.response?.data?.message || error.message || 'Failed to export workflows.';
    toast.error(errorMessage, {
      autoClose: 2000,
    });
  } finally {
    loader.hide();
  }
};

const getWorkflowCategories = async () => {
  let loader = $loading.show();

  axios
    .get("/workflow-categories?status=active&all=1")
    .then((response) => {
      if (response.status === "success") {
        workflowCategories.value = normalizeListData(response.data);
      } else {
        workflowCategories.value = [];
        toast.error(response.message, {
          autoClose: 2000,
        });
      }
    })
    .catch(() => {
      workflowCategories.value = [];
      toast.error("Failed to fetch workflow categories.", {
        autoClose: 2000,
      });
    })
    .finally(() => {
      loader.hide();
    });
};

const getAvailableWorkflowsByCategory = async (payload = null) => {
  if (!selectedWorkflowCategory.value?.id) {
    availableWorkflows.value = { data: [], total: 0 };
    return;
  }

  const requestPayload = payload || availableWorkflowFilterInfo.value || { page: 1, perPage: 10 };
  const safePage = requestPayload.page || 1;
  const safePerPage = requestPayload.perPage || 10;

  let url = `/available_workflows?page=${safePage}&per_page=${safePerPage}&workflow_category_id=${selectedWorkflowCategory.value.id}`;

  if (requestPayload.search && requestPayload.search.trim() !== "") {
    url += `&search=${encodeURIComponent(requestPayload.search.trim())}`;
  }

  if (requestPayload.sortBy && requestPayload.orderBy) {
    url += `&sort_by=${requestPayload.sortBy}&order_by=${requestPayload.orderBy}`;
  }

  let loader = $loading.show();
  axios
    .get(url)
    .then((response) => {
      if (response.status === "success") {
        availableWorkflows.value = response?.data?.workflows || { data: [], total: 0 };
      } else {
        availableWorkflows.value = { data: [], total: 0 };
        toast.error(response.message, {
          autoClose: 2000,
        });
      }
    })
    .catch(() => {
      availableWorkflows.value = { data: [], total: 0 };
      toast.error("Failed to fetch available workflows.", {
        autoClose: 2000,
      });
    })
    .finally(() => {
      loader.hide();
    });
};

const openAvailableWorkflowModal = async (category) => {
  selectedWorkflowCategory.value = category;
  availableWorkflowFilterInfo.value = { page: 1, perPage: 10 };
  isAvailableWorkflowModalVisible.value = true;

  await getAvailableWorkflowsByCategory(availableWorkflowFilterInfo.value);
};

const closeAvailableWorkflowModal = () => {
  isAvailableWorkflowModalVisible.value = false;
};

const getWorkflows = async (payload = null) => {
  if (currentTab.value === 'available') {
    return;
  }

  const requestPayload = payload || filterInfo.value || { page: 1, perPage: 10 };
  const safePage = requestPayload.page || 1;
  const safePerPage = requestPayload.perPage || 10;
  const endpoint = getApiEndpoint();
  let url = `${endpoint}?page=${safePage}&per_page=${safePerPage}`;
  
  if (requestPayload.search && requestPayload.search.trim() !== "") {
    url += `&search=${encodeURIComponent(requestPayload.search.trim())}`;
  }
  
  if (requestPayload.sortBy && requestPayload.orderBy) {
    url += `&sort_by=${requestPayload.sortBy}&order_by=${requestPayload.orderBy}`;
  }
  
  let loader = $loading.show();
  axios
    .get(url)
    .then((response) => {
      if (response.status == "success") {
        workflows.value = response?.data?.workflows || [];
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      } else {
        toast.error(response.message, {
          autoClose: 2000,
        });
      }
    })
    .catch((error) => {
      console.error('Error fetching workflows:', error);
      toast.error('Failed to fetch workflows.', {
        autoClose: 2000,
      });
      loader.hide();
    })
    .finally(() => {
      loader.hide();
    });
};

// Page changed from pagination
const tableChanged = async (payload) => {
  if (currentTab.value === 'available') {
    availableWorkflowFilterInfo.value = { ...availableWorkflowFilterInfo.value, ...payload };
    getAvailableWorkflowsByCategory(availableWorkflowFilterInfo.value);
    return;
  }

  filterInfo.value = { ...filterInfo.value, ...payload };
  getWorkflows(filterInfo.value);
};

// Tab changed - similar to tickets.vue
const filterListChanged = async () => {
  if (currentTab.value === 'available') {
    closeAvailableWorkflowModal();
    selectedWorkflowCategory.value = null;
    availableWorkflows.value = { data: [], total: 0 };
    await getWorkflowCategories();
    return;
  }

  filterInfo.value = { page: 1, perPage: 10 };
  getWorkflows(filterInfo.value);
};

// Watch for tab changes - similar to tickets.vue
watch(
  () => currentTab.value,
  () => {
    filterListChanged();
  }
);

// load workflows when page loads
onMounted(async () => {
  filterInfo.value = { page: 1, perPage: 10 };
  getWorkflows(filterInfo.value);
});

definePage({ meta: { requiresAuth: true } });
</script>

<template>
  <div>
    <VTabs v-model="currentTab" class="v-tabs-pill mb-2">
      <VTab key="all" value="all">All Workflows</VTab>
      <VTab key="completed" value="completed">Completed Workflows</VTab>
      <VTab key="my" value="my">My Workflows</VTab>
      <VTab key="approvals" value="approvals">My Approval</VTab>
      <VTab key="assigned" value="assigned">Assigned Workflows</VTab>
      <VTab key="available" value="available">Available Workflows</VTab>
    </VTabs>

    <div class="d-flex justify-end mb-2">
      <VBtn
        color="primary"
        variant="tonal"
        prepend-icon="tabler-file-type-csv"
        @click="exportWorkflows"
      >
        Export
      </VBtn>
    </div>

    <VCard>
      <VCardText
        class="p-0"
        :style="currentTab === 'available' ? 'padding: 20px !important' : 'padding: 0 !important'"
      >
        <template v-if="currentTab === 'available'">
          <VRow>
            <VCol
              v-for="category in workflowCategories"
              :key="category.id"
              cols="12"
              md="4"
              lg="3"
            >
              <VCard
                border
                class="workflow-category-card"
                @click="openAvailableWorkflowModal(category)"
              >
                <VCardText class="d-flex justify-space-between align-center">
                  <div>
                    <h5 class="text-h6">{{ category.name }}</h5>
                    <span class="text-sm text-medium-emphasis">Use workflows from this category</span>
                  </div>
                  <VBtn
                    size="36"
                    icon="tabler-arrow-right"
                    rounded
                    variant="tonal"
                  />
                </VCardText>
              </VCard>
            </VCol>

            <VCol v-if="workflowCategories.length === 0" cols="12">
              <VAlert type="info" variant="tonal">
                No workflow categories available for your account.
              </VAlert>
            </VCol>
          </VRow>
        </template>

        <template v-else>
        <WorkflowsDataTableList
          :key="currentTab"
          :headers="headers"
          :table-data="workflows"
          :current-tab="currentTab"
          @tableChanged="tableChanged"
          @startWorkflow="startWorkflow"
          @viewWorkflow="viewWorkflow"
          @goToApprovalStep="goToApprovalStep"
        />
        </template>
      </VCardText>
    </VCard>

    <VDialog
      v-model="isAvailableWorkflowModalVisible"
      max-width="1200"
      scrollable
    >
      <VCard>
        <VCardTitle class="d-flex justify-space-between align-center">
          <span>{{ availableWorkflowModalTitle }}</span>
          <VBtn
            icon="tabler-x"
            variant="text"
            @click="closeAvailableWorkflowModal"
          />
        </VCardTitle>
        <VDivider />
        <VCardText class="p-0">
          <WorkflowsDataTableList
            :key="`available-${selectedWorkflowCategory?.id || 'none'}`"
            :headers="headers"
            :table-data="availableWorkflows"
            current-tab="available"
            @tableChanged="tableChanged"
            @startWorkflow="startWorkflow"
            @viewWorkflow="viewWorkflow"
            @goToApprovalStep="goToApprovalStep"
          />
        </VCardText>
      </VCard>
    </VDialog>

    <!-- License required error popup -->
    <VDialog
      v-model="isLicenseErrorModalVisible"
      max-width="420"
    >
      <VCard>
        <VCardItem class="pb-0">
          <VCardTitle class="d-flex align-center gap-2 text-error">
            <VIcon icon="tabler-license" />
            License Required
          </VCardTitle>
        </VCardItem>
        <VCardText class="pt-4">
          <p class="mb-0">{{ licenseErrorMessage }}</p>
        </VCardText>
        <VCardActions class="justify-end">
          <VBtn color="primary" @click="isLicenseErrorModalVisible = false">
            OK
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>

<style scoped>
.workflow-category-card {
  min-height: 108px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.workflow-category-card:hover {
  background-color: #71758d17;
}
</style>
